// @ts-nocheck
import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { Autocomplete, TextField } from '@mui/material';

import { ReactComponent as DropdownArrow } from '../../../../assets/images/DropdownArrow.svg';
import { useConstantAttributeListQuery } from '../../../../features/configuration/constantAttributes/api/constantAttributesQueries';
import { getErrorHelperText } from '../../../../utils/Utils';
import { EMPTY_ARRAY } from '../../../../const/CommonConst';
import { DRAFT_INPUT_SX } from './TextFieldComponent';

const getParameterTableOptionLabel = (option) => option?.name;
const isOptionEqualToValue = (option, value) =>
  String(option?.id) === String(value?.id);

const ParameterTableFieldComponent = ({
  label,
  titleLabel,
  value,
  onChange,
  attribute,
  entityDetailsFormikForm,
  required = false,
  hasDraftChip = false,
  onDraftBlur,
}) => {
  const { data: rawConstantAttributesRes } = useConstantAttributeListQuery({
    params: { minimal: true },
  });

  const parameterTableList = useMemo(() => {
    if (Array.isArray(rawConstantAttributesRes)) {
      return rawConstantAttributesRes;
    }
    return rawConstantAttributesRes?.results || EMPTY_ARRAY;
  }, [rawConstantAttributesRes]);

  const filteredList = useMemo(() => {
    return parameterTableList.filter((item) => item?.data_type === 'DataFrame');
  }, [parameterTableList]);

  const selectedOption = useMemo(() => {
    return (
      filteredList.find((option) => String(option?.id) === String(value?.id)) ||
      null
    );
  }, [filteredList, value]);

  const handleChange = useCallback(
    (_event, newValue) => {
      if (newValue) {
        onChange({
          id: newValue?.id,
          name: newValue?.name,
        });
      } else {
        onChange(null);
      }
    },
    [onChange]
  );

  const handleClose = useCallback(() => {
    onDraftBlur?.();
  }, [onDraftBlur]);

  const renderInput = useCallback(
    (params) => (
      <TextField
        {...params}
        label={label}
        title={titleLabel}
        required={required}
        placeholder="Select parameter table"
        slotProps={{
          ...params?.slotProps,
          input: {
            ...params?.slotProps?.input,
            placeholder: 'Select parameter table',
          },

          inputLabel: {
            ...params?.slotProps?.inputLabel,
            shrink: true,
          },
        }}
        error={Boolean(
          getErrorHelperText(
            entityDetailsFormikForm?.errors?.[attribute?.id],
            entityDetailsFormikForm?.touched?.[attribute?.id]
          )
        )}
        helperText={getErrorHelperText(
          entityDetailsFormikForm?.errors?.[attribute?.id],
          entityDetailsFormikForm?.touched?.[attribute?.id]
        )}
        sx={hasDraftChip ? DRAFT_INPUT_SX : undefined}
      />
    ),
    [
      attribute?.id,
      entityDetailsFormikForm,
      label,
      required,
      titleLabel,
      hasDraftChip,
    ]
  );

  return (
    <Autocomplete
      value={selectedOption}
      options={filteredList}
      onChange={handleChange}
      onClose={handleClose}
      noOptionsText="No parameter tables present."
      popupIcon={<DropdownArrow />}
      getOptionLabel={getParameterTableOptionLabel}
      isOptionEqualToValue={isOptionEqualToValue}
      renderInput={renderInput}
    />
  );
};

ParameterTableFieldComponent.propTypes = {
  label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
  titleLabel: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.shape({}),
  ]),
  onChange: PropTypes.func.isRequired,
  required: PropTypes.bool,
  attribute: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }).isRequired,
  entityDetailsFormikForm: PropTypes.shape({
    setFieldTouched: PropTypes.func,
    errors: PropTypes.objectOf(PropTypes.oneOfType([PropTypes.string])),
    touched: PropTypes.objectOf(PropTypes.bool),
  }),
  hasDraftChip: PropTypes.bool,
  onDraftBlur: PropTypes.func,
};

export default ParameterTableFieldComponent;
